/**
 * Builds the markdown representation of a PlatinumData table that is fed to
 * the Tables Insights chat model (see `prompts.GetTableAnalysisPrompt` in
 * goapi and `useTableChat.ts`).
 *
 * #977: row labels here MUST be the same strings the chart-rendering code
 * matches against (`extractRowLabels` / `convertPlatinumToTable` in
 * `components/platinum-graphs/utils/convertPlatinumData.ts`) — i.e. the
 * plain `vec.label`, not a table-hierarchy path.
 *
 * The previous implementation built each row's markdown label from
 * `getCellHierarchy(...).sideHierarchy`, joining every side GROUP header
 * above the row with the leaf label (e.g.
 * "!BrandUsage from all questions & categories(Sky) > Spontaneous awareness
 * Of brands > Sky" for a table with per-brand side groups). The model was
 * told to copy row labels "EXACTLY as they appear in the markdown table",
 * so it dutifully echoed that whole hierarchy string back as
 * `selectedRows` — which never matches `extractRowLabels()` (just "Sky"),
 * so `filterTableData` filtered every row out and the chart pane showed
 * "Filtering removed all rows. No data to display." on the very first
 * filtered follow-up question. Using the plain leaf label here eliminates
 * that mismatch: whatever exact string the model copies from the table it
 * was shown is guaranteed to resolve later.
 */
import type { PlatinumData } from '../types/carbon/models/PlatinumData'

// Carbon's percentages are proportions (0.25); shown as "25%" so no reader,
// the agent included, takes 0.25 for 0.25% (a 100x error). Counts stay bare.
const pct = (p: number) => `${Math.round(p * 1000) / 10}%`

export function getTableMarkdown(data: PlatinumData | undefined, maxRows?: number): string | null {
  if (!data || !data.cells?.rows || !data.top?.vecs) return null

  const sideVecs = data.side?.vecs || []
  const rows = maxRows === undefined ? data.cells.rows : data.cells.rows.slice(0, maxRows)

  // Column headers — already plain vec labels, matching extractColumnLabels()
  const headers = [''].concat((data.top.vecs || []).map((v) => v.label || 'Col'))
  let md = `| ${headers.join(' | ')} |\n|${headers.map(() => '---').join('|')}|\n`

  rows.forEach((row, vecIndex) => {
    const vec = sideVecs[vecIndex]
    const vecLabel = vec?.label || `Row ${vecIndex + 1}`
    const vecType = (vec?.type || (vec as any)?.Type) as string | undefined

    // Match extractRowLabels()'s Base+Total -> "Weighted Total" rename so the
    // label shown to the model is identical to what the chart matcher uses.
    const fullLabel = vecType?.toLowerCase() === 'base' && vecLabel === 'Total'
      ? 'Weighted Total'
      : vecLabel

    const vals = (row.cell || []).map((c: any) => {
      const freq = c?.freq ?? c?.Freq
      const colpc = c?.colpc ?? c?.Colpc
      const rowpc = c?.rowpc ?? c?.Rowpc

      if (vecType === 'Base' || vecType === 'Function') {
        return freq ?? ''
      }

      // Prefer column percent; fallback to the count, then row percent
      if (typeof colpc === 'number') return pct(colpc)
      if (freq !== undefined && freq !== null) return freq
      if (typeof rowpc === 'number') return `${pct(rowpc)} (row)`
      return ''
    })

    md += `| ${[fullLabel, ...vals].join(' | ')} |\n`
  })

  return md
}
